/**
 * Dominio de Notificaciones in-app (Fase 1).
 *
 * La campanita de la topbar sondea el contador de no leídas por intervalo
 * (refetchInterval) para mantenerse fresca sin websockets. La lista completa
 * se trae al abrir el dropdown. Todo filtra por empresa en el backend.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from './api';

export interface AppNotification {
  id: string;
  type: string;
  title: string;
  body: string;
  /** Ruta interna (relativa al basename) a la que navegar al hacer click. */
  link: string | null;
  is_read: boolean;
  created_at: string;
}

export const notificationsKey = ['notifications'] as const;
export const unreadCountKey = ['notifications', 'unread-count'] as const;

/** Sondeo del contador de no leídas cada 30s. */
const UNREAD_POLL_MS = 30_000;

export function useNotifications(enabled = true) {
  return useQuery({
    queryKey: notificationsKey,
    queryFn: () => api.get<AppNotification[]>('/notifications'),
    enabled,
  });
}

export function useUnreadCount() {
  return useQuery({
    queryKey: unreadCountKey,
    queryFn: () => api.get<{ count: number }>('/notifications/unread-count'),
    refetchInterval: UNREAD_POLL_MS,
    refetchIntervalInBackground: false,
    staleTime: 0,
  });
}

export function useMarkRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<unknown>(`/notifications/${id}/read`),
    onSuccess: (_res, id) => {
      qc.setQueryData<AppNotification[]>(notificationsKey, (old) =>
        old ? old.map((n) => (n.id === id ? { ...n, is_read: true } : n)) : old,
      );
      void qc.invalidateQueries({ queryKey: unreadCountKey });
    },
  });
}

export function useMarkAllRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<unknown>('/notifications/read-all'),
    onSuccess: () => {
      qc.setQueryData<AppNotification[]>(notificationsKey, (old) =>
        old ? old.map((n) => ({ ...n, is_read: true })) : old,
      );
      qc.setQueryData<{ count: number }>(unreadCountKey, { count: 0 });
    },
  });
}
